import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from './MarketingLayout';

const CATEGORIES = [
  {
    icon: '⚡',
    title: 'Getting Started',
    color: '#2563eb',
    articles: [
      { title: 'How to create your first campaign', desc: 'Set up click fraud protection in under 5 minutes.', slug: 'creating-first-campaign' },
      { title: 'Installing the ClickSaver tracking script', desc: 'Add the JavaScript snippet to your website or landing page.', slug: 'installing-tracking-script' },
      { title: 'Connecting Google Ads', desc: 'Link your Google Ads account to enable automatic IP exclusions.', slug: 'connecting-google-ads' },
      { title: 'Understanding your dashboard', desc: 'A walkthrough of all dashboard sections and what they mean.', slug: 'understanding-fraud-report' },
    ],
  },
  {
    icon: '🛡',
    title: 'Fraud Detection',
    color: '#7c3aed',
    articles: [
      { title: 'How click fraud detection works', desc: 'Learn about the signals we use to identify invalid clicks.', slug: 'fraud-detection-signals' },
      { title: 'Setting detection thresholds', desc: 'Customise click limits per 10 minutes, day, and month.', slug: 'adjusting-thresholds' },
      { title: 'VPN, Tor, and datacenter blocking', desc: 'Automatically block traffic from anonymising services.', slug: 'custom-detection-rules' },
      { title: 'Whitelisting legitimate IPs', desc: 'Exclude your own team or known partners from detection.', slug: 'whitelisting-ips' },
    ],
  },
  {
    icon: '◎',
    title: 'Google Ads Sync',
    color: '#059669',
    articles: [
      { title: 'How IP exclusions are pushed to Google Ads', desc: 'Understand the sync flow from blocked IP to Google exclusion list.', slug: 'how-ip-exclusions-work' },
      { title: 'Setting up auto-sync', desc: 'Configure hourly, daily, weekly, or monthly automatic syncs.', slug: 'campaign-level-exclusions' },
      { title: 'Linking a campaign to Google Ads', desc: 'Map a ClickSaver campaign to a specific Google Ads campaign.', slug: 'campaign-level-exclusions' },
      { title: 'Troubleshooting sync errors', desc: 'Common sync issues and how to resolve them.', slug: 'verifying-blocks-working' },
    ],
  },
  {
    icon: '💳',
    title: 'Billing & Account',
    color: '#d97706',
    articles: [
      { title: 'Changing your subscription plan', desc: 'Upgrade, downgrade, or cancel your plan at any time.', slug: 'changing-plan' },
      { title: 'Downloading invoices', desc: 'Access and download all past invoices from Settings.', slug: 'understanding-invoice' },
      { title: 'Updating payment details', desc: 'Change your credit card or billing information.', slug: 'updating-payment' },
      { title: 'Sharing account access', desc: 'Add team members to your ClickSaver account.', slug: 'custom-detection-rules' },
    ],
  },
];

const FAQS = [
  { q: 'How long does it take for blocked IPs to appear in Google Ads?', a: 'With auto-sync enabled, blocked IPs are pushed to Google Ads within your chosen interval (hourly being the fastest). You can also trigger an immediate sync any time from Settings or the Google Ads Sync page.' },
  { q: 'Will blocking IPs affect my legitimate traffic?', a: 'No. ClickSaver only blocks IPs that exceed your configured fraud thresholds. You can whitelist known IPs (your office, team members) to ensure they are never blocked.' },
  { q: 'What happens when I reach my click limit?', a: 'If you exceed your plan\'s monthly click limit, fraud detection continues but new click data may not be stored. Upgrade your plan to increase your limit.' },
  { q: 'Can I export my blocked IP list?', a: 'Yes — go to Blocked IPs in your dashboard and use the export button to download the full list as a CSV.' },
  { q: 'Is ClickSaver GDPR compliant?', a: 'Yes. We collect only the minimum data needed for fraud detection (IP address, user agent, timestamps). IP addresses are pseudonymised after 30 days. See our Privacy Policy for full details.' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #e2e8f0' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '18px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', textAlign: 'left' }}>{q}</span>
        <span style={{ fontSize: 18, color: '#64748b', flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(45deg)' : 'none' }}>+</span>
      </button>
      {open && (
        <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.7, paddingBottom: 18 }}>{a}</div>
      )}
    </div>
  );
}

export default function Support() {
  return (
    <MarketingLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '60px 24px 80px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600, color: '#2563eb', marginBottom: 16 }}>
            HELP CENTER
          </div>
          <h1 style={{ fontSize: 42, fontWeight: 800, color: '#0f172a', marginBottom: 16, lineHeight: 1.2 }}>
            How can we help?
          </h1>
          <p style={{ fontSize: 17, color: '#64748b', maxWidth: 520, margin: '0 auto 28px', lineHeight: 1.7 }}>
            Browse guides, FAQs, and troubleshooting docs — or reach us directly at{' '}
            <a href="mailto:support@clicksaver.app" style={{ color: '#2563eb', fontWeight: 600 }}>support@clicksaver.app</a>
          </p>

          {/* Quick contact CTA */}
          <Link
            to="/contact"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#2563eb', color: '#fff', textDecoration: 'none',
              padding: '12px 28px', borderRadius: 10, fontSize: 14, fontWeight: 700,
            }}
          >
            ✉ Contact Support
          </Link>
        </div>

        {/* Categories */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 64 }}>
          {CATEGORIES.map((cat, i) => (
            <div key={i} style={{
              background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0',
              padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              transition: 'box-shadow 0.2s',
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>{cat.icon}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>{cat.title}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {cat.articles.map((a, j) => (
                  <Link
                    key={j}
                    to={`/knowledge-center/${a.slug}`}
                    style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, padding: '10px 10px', borderRadius: 8, textDecoration: 'none', transition: 'background 0.15s', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.querySelector('.arr').style.opacity = '1'; e.currentTarget.querySelector('.arr').style.transform = 'translateX(2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.arr').style.opacity = '0'; e.currentTarget.querySelector('.arr').style.transform = 'translateX(0)'; }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: cat.color, marginBottom: 2 }}>{a.title}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>{a.desc}</div>
                    </div>
                    <span className="arr" style={{ color: cat.color, fontSize: 14, flexShrink: 0, marginTop: 2, opacity: 0, transition: 'opacity 0.15s, transform 0.15s' }}>→</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* FAQs */}
        <div style={{ maxWidth: 740, margin: '0 auto 64px' }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 8, textAlign: 'center' }}>Frequently Asked Questions</h2>
          <p style={{ fontSize: 15, color: '#64748b', textAlign: 'center', marginBottom: 36 }}>Can't find your answer? Email us at support@clicksaver.app</p>
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '0 28px' }}>
            {FAQS.map((faq, i) => <FAQItem key={i} {...faq} />)}
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
          borderRadius: 16, padding: '40px 40px', textAlign: 'center',
        }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Still need help?</h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.8)', marginBottom: 24 }}>
            Our support team responds within 24 hours on business days.
          </p>
          <a
            href="mailto:support@clicksaver.app"
            style={{
              display: 'inline-block', background: '#fff', color: '#2563eb',
              textDecoration: 'none', padding: '12px 32px', borderRadius: 10,
              fontSize: 14, fontWeight: 700,
            }}
          >
            Email support@clicksaver.app
          </a>
        </div>

      </div>
    </MarketingLayout>
  );
}
